import React, { ElementType, ReactElement } from 'react';
import { useMemorySafeTimeout } from '../../../hooks/useMemorySafeTimout';

interface iProps {
  children: any;
  shouldRender: boolean;
  transitionDuration: number;
  onFinishCallback?: () => void;
  tag?: ElementType;
}

const RenderDelay = ({
  children,
  shouldRender,
  transitionDuration,
  onFinishCallback,
  tag: Tag = 'div',
}: iProps) => {
  const setSafeTimeout = useMemorySafeTimeout();

  /** Track which element should be animated */
  const [tick, setTick] = React.useState(-1);
  /** Stores children with animation to be rendered */
  const [childrenToRender, setChildrenToRender] = React.useState([]);

  /** Checks the passed props and starts iterating */
  React.useEffect(() => {
    if (shouldRender) {
      setTick(tick + 1);
    } else {
      setTick(-1);
      setChildrenToRender([]);
    }
  }, [shouldRender]);

  React.useEffect(() => {
    const updatedChildren = children.map((child, index) => {
      return index === tick ? (
        <Tag key={index} className={'animate-top'}>
          {child}
        </Tag>
      ) : index < tick ? (
        <Tag key={index} className={'animate-top'}>
          {child}
        </Tag>
      ) : (
        <Tag key={index} className={'hidden'}>
          {child}
        </Tag>
      );
    });

    /** Filters null children, to make prevent unnecessary iterations */
    setChildrenToRender(
      updatedChildren.filter((child: ReactElement | null) => !!child)
    );
  }, [tick, children]);

  React.useEffect(() => {
    if (shouldRender && tick === children.length) {
      onFinishCallback && onFinishCallback();
    }
  }, [tick]);

  React.useEffect(() => {
    if (shouldRender && tick < children.length) {
      setSafeTimeout(() => {
        setTick(tick + 1);
      }, transitionDuration);
    }
  }, [childrenToRender]);

  return <>{childrenToRender}</>;
};

export default RenderDelay;
